import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, Subject, of, throwError} from 'rxjs';
import {catchError, shareReplay, startWith, switchMap, tap} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {environment} from '../../environments/environment';
import {ProjectLink} from '../../models/project-analysis';
import {ToastService} from '../toast/toast.service';

@Injectable({
    providedIn: 'root'
})
export class ProjectLinkService {
    private readonly httpClient = inject(HttpClient);
    private readonly toastService = inject(ToastService);
    private readonly translateService = inject(TranslateService);
    private readonly baseUrl = `${environment.apiUrl}/links`;

    private readonly reload$ = new Subject<void>();

    /**
     * Global links shown in the project action menu. Shared while subscribed and refetched on the
     * next subscription, so a failed load is not cached. Fails silently: the menu just shows no links.
     * Never completes, one-shot consumers must pipe take(1).
     */
    public readonly links$: Observable<ProjectLink[]> = this.reload$.pipe(
        startWith(undefined as void),
        switchMap(() => this.httpClient.get<ProjectLink[]>(this.baseUrl).pipe(
            catchError(() => of([] as ProjectLink[]))
        )),
        shareReplay({bufferSize: 1, refCount: true})
    );

    public add(data: ProjectLink): Observable<ProjectLink> {
        return this.httpClient.post<ProjectLink>(this.baseUrl, data).pipe(
            tap(() => this.reload$.next()),
            catchError((error) => this.handleError(error, 'ERRORS.project_link_not_added'))
        );
    }

    public update(id: number, data: ProjectLink): Observable<ProjectLink> {
        return this.httpClient.post<ProjectLink>(`${this.baseUrl}/${id}`, data).pipe(
            tap(() => this.reload$.next()),
            catchError((error) => this.handleError(error, 'ERRORS.project_link_not_updated'))
        );
    }

    public delete(id: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.baseUrl}/${id}`).pipe(
            tap(() => this.reload$.next()),
            catchError((error) => this.handleError(error, 'ERRORS.project_link_not_deleted'))
        );
    }

    private handleError(error: { status?: number }, translationKey: string): Observable<never> {
        this.notifyError(error, translationKey);

        return throwError(() => error);
    }

    private notifyError(error: { status?: number }, translationKey: string): void {
        this.toastService.openError(error.status ?? 0, this.translateService.instant(translationKey));
    }
}
